// src/components/GlassSmileHero.tsx
// Yumuşak stüdyo gradyanı zemin. Masaüstünde kliniğin gerçek 360°
// panoraması (ToothScene), mobilde soyut/animasyonlu bir ışık kompozisyonu
// (MobileHeroBackground) arka planda kalıyor - fotoğraf/video denemeleri
// (panorama, çeşitli klipler) mobilde ya kırpma ya kalite sorunu çıkardığı
// için soyut bir tasarıma geçildi. Ortadaki diş/implant 3D modeli kaldırıldı
// (kullanıcı isteğiyle - görünümü beğenilmedi). Sol tarafta/CTA üstünde
// adım kutuları (1. Randevu / 2. Optimum Tedavi / 3. Ücretsiz Muayene -
// HeroStepCards), sağ tarafta gömülü Medicasimple randevu paneli.
import React, { Suspense, lazy, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import MagneticButton from "./MagneticButton";
import { MEDICASIMPLE_BOOKING_URL } from "../config/booking";
import atlasGlobeHero from "../assets/atlas-globe-hero.webp";

// Masaüstü: ağır 3D panorama sahnesi. Mobil: hafif soyut arka plan -
// ikisi de ayrı lazy chunk, ilgisiz olan hiç indirilmiyor.
const ToothScene = lazy(() => import("./ToothScene"));
const MobileHeroBackground = lazy(() => import("./MobileHeroBackground"));

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(motionQuery.matches);
    const handler = () => setReduced(motionQuery.matches);
    motionQuery.addEventListener("change", handler);
    return () => motionQuery.removeEventListener("change", handler);
  }, []);

  return reduced;
}

// Başlangıç değeri lazy initializer ile SENKRON hesaplanıyor (useEffect
// beklemeden) - aksi halde ilk render'da "false" varsayılıp masaüstü
// ToothScene chunk'ı bir an için mobilde de indirilmeye başlayabiliyordu.
function computeLowPower(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 768;
}

function useIsLowPowerDevice(): boolean {
  const [low, setLow] = useState(computeLowPower);

  useEffect(() => {
    const coarsePointer = window.matchMedia("(pointer: coarse)");
    const handler = () => setLow(coarsePointer.matches || window.innerWidth < 768);
    coarsePointer.addEventListener("change", handler);
    window.addEventListener("resize", handler);
    return () => {
      coarsePointer.removeEventListener("change", handler);
      window.removeEventListener("resize", handler);
    };
  }, []);

  return low;
}

const GlassSmileHero: React.FC = () => {
  const { t } = useTranslation();
  const reduceMotion = usePrefersReducedMotion();
  const lowPower = useIsLowPowerDevice();

  return (
    <>
      <section
        className="relative w-full h-screen overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at 50% 42%, #4a4a4a 0%, #302f2e 45%, #171615 78%, #0a0a09 100%)",
        }}
      >
        {/* Başlık + Atlas illüstrasyonu tek akış içinde - resim, başlığın
            hemen altında normal margin ile diziliyor (sabit/merkezi
            konumlandırma yerine) böylece hiçbir ekran boyutunda üstteki
            yazıyla üst üste binmiyor. 768-1023px (md, tablet) aralığında
            sol/sağ panellerle sıkışmaması için o aralıkta gizli. */}
        <motion.div
          initial={{ y: -16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="absolute top-24 sm:top-20 md:top-24 left-0 right-0 text-center px-6 z-10"
        >
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-2 md:mb-3">
            {t("hero.title")}
          </h1>
          <p className="text-sm sm:text-base md:text-xl text-white/70 max-w-xl mx-auto">
            {t("hero.subtitle")}
          </p>

          {/* Atlas illüstrasyonu + alıntı SADECE masaüstünde (lg+) - tablette
              (md-lg arası) gizli. flex+justify-center ile kesin yatay
              ortalama - mx-auto yerine (her tarayıcıda/genişlikte garanti
              ortalı kalması için). */}
          <div className="hidden lg:flex w-full justify-center mt-4 sm:mt-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
              className="relative w-[150px] sm:w-[180px] lg:w-[210px] xl:w-[250px] pointer-events-none"
            >
              <img
                src={atlasGlobeHero}
                alt=""
                aria-hidden="true"
                role="presentation"
                className="w-full h-auto select-none"
                style={{ filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.6))" }}
              />
              {/* Küre boşluğuna tam ortalanmış kutu - sabit koordinat yerine
                  flex ile ortalıyoruz ki metin uzunluğu değişse bile hep
                  küre içinde dik-yatay ortalı kalsın. Renk navbar linkleriyle
                  aynı (yellow-500). */}
              <div
                className="absolute left-0 right-0 flex items-center justify-center px-4"
                style={{ top: "4%", height: "40%" }}
              >
                <p className="font-serif italic text-yellow-500 text-center text-[10px] sm:text-[11px] lg:text-xs leading-snug">
                  &ldquo;{t("hero.quote")}&rdquo;
                </p>
              </div>
            </motion.div>
          </div>

        </motion.div>

        {/* Atlas illüstrasyonu + alıntı - SADECE mobilde, masaüstündeki
            (lg+) bloktan bağımsız ayrı bir kopya (web tarafına dokunulmadı).
            Başlığın akışından çıkarılıp ekranın TAM ortasına (dikey+yatay)
            sabitlendi, kullanıcı isteğiyle daha büyük. Başlık yukarıda
            (top-24) sabit durduğu ve bu blok ekranın merkezinde kaldığı
            için normal boy ekranlarda çakışma olmuyor. */}
        <div className="flex md:hidden absolute inset-0 items-center justify-center pointer-events-none z-[6]">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
            className="relative w-[230px] sm:w-[270px] pointer-events-none"
          >
            <img
              src={atlasGlobeHero}
              alt=""
              aria-hidden="true"
              role="presentation"
              className="w-full h-auto select-none"
              style={{ filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.6))" }}
            />
            <div
              className="absolute left-0 right-0 flex items-center justify-center px-4"
              style={{ top: "4%", height: "40%" }}
            >
              <p className="font-serif italic text-yellow-500 text-center text-xs sm:text-sm leading-snug">
                &ldquo;{t("hero.quote")}&rdquo;
              </p>
            </div>
          </motion.div>
        </div>

        {/* Masaüstü: 3D panorama + adım kutuları (ToothScene). Mobil: soyut/
            animasyonlu ışık kompozisyonu + aynı adım kutuları
            (MobileHeroBackground) - fotoğraf/video denemeleri mobilde
            kırpma/kalite sorunu çıkardığı için bu tasarıma geçildi, ayrıca
            three.js bundle'ı da hiç inmiyor. */}
        <div className="absolute inset-0">
          {!reduceMotion && (
            <Suspense fallback={null}>
              {lowPower ? <MobileHeroBackground /> : <ToothScene />}
            </Suspense>
          )}
        </div>

        {/* CTA - mobilde alt ortada; masaüstünde sağdaki gömülü randevu
            paneli birincil yol olduğu için orada gizleniyor. */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
          className="flex md:hidden absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
        >
          <MagneticButton
            href={MEDICASIMPLE_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-yellow-600 text-black font-semibold px-8 py-3.5 rounded-lg hover:bg-yellow-500 transition-colors"
          >
            {t("hero.button")}
          </MagneticButton>
        </motion.div>

        {/* Masaüstünde sağda, gömülü Medicasimple randevu paneli - sol taraf
            ToothScene'in kendi adım kutularıyla dolu olduğu için sağda. */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
          className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 z-20 flex-col w-[360px] lg:w-[400px] rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md p-4 shadow-2xl"
        >
          <h3 className="text-white font-semibold text-sm uppercase tracking-wide mb-1">
            {t("contact.bookOnline")}
          </h3>
          <p className="text-white/60 text-xs mb-3">{t("contact.bookOnlineSubtitle")}</p>
          <div className="w-full rounded-xl overflow-hidden bg-white">
            <iframe
              src={MEDICASIMPLE_BOOKING_URL}
              width="100%"
              height="440"
              style={{ border: 0 }}
              loading="lazy"
              title="Medicasimple Online Randevu"
            />
          </div>
        </motion.div>
      </section>

      {/* Mobilde yan yana yer olmadığı için panel hero'nun hemen altında,
          tam genişlikte, normal akışta beliriyor. */}
      <div className="md:hidden bg-[#171615] px-6 pt-8 pb-10">
        <h3 className="text-white font-semibold text-sm uppercase tracking-wide mb-1 text-center">
          {t("contact.bookOnline")}
        </h3>
        <p className="text-white/60 text-xs mb-4 text-center">{t("contact.bookOnlineSubtitle")}</p>
        <div className="w-full rounded-xl overflow-hidden bg-white">
          <iframe
            src={MEDICASIMPLE_BOOKING_URL}
            width="100%"
            height="480"
            style={{ border: 0 }}
            loading="lazy"
            title="Medicasimple Online Randevu"
          />
        </div>
      </div>
    </>
  );
};

export default GlassSmileHero;
